import { useState } from 'react';
import { toast } from 'react-toastify';
import { putUnitStrataManagers } from 'src/services/client';
import { variables } from 'src/Tenants';
import {
  IStrataManager,
  IUnitAssignmentUpdate,
  IUnitStrataAssignments,
} from 'src/pages/Property/ManageStrataManagers/types';

interface UseSuiteManagerCardOrchestratorArgs {
  unit: IUnitStrataAssignments;
}

export interface UseSuiteManagerCardOrchestratorReturn {
  assignedManagers: IStrataManager[];
  isDirty: boolean;
  isSaving: boolean;
  addManagers: (managers: IStrataManager[]) => void;
  removeManager: (userId: string) => void;
  save: () => Promise<void>;
}

export const useSuiteManagerCardOrchestrator = ({
  unit,
}: UseSuiteManagerCardOrchestratorArgs): UseSuiteManagerCardOrchestratorReturn => {
  const [assignedManagers, setAssignedManagers] = useState<IStrataManager[]>(
    unit.assignedStrataManagers
  );
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const addManagers = (managers: IStrataManager[]): void => {
    const existingIds: Set<string> = new Set(
      assignedManagers.map((m: IStrataManager) => m.userId)
    );

    const toAdd: IStrataManager[] = managers.filter(
      (m: IStrataManager) => !existingIds.has(m.userId)
    );

    if (toAdd.length === 0) {
      return;
    }

    setAssignedManagers([...assignedManagers, ...toAdd]);
    setIsDirty(true);
  };

  const removeManager = (userId: string): void => {
    const updatedManagers: IStrataManager[] = assignedManagers.filter(
      (m: IStrataManager) => m.userId !== userId
    );

    setAssignedManagers(updatedManagers);
    setIsDirty(true);
  };

  const save = async (): Promise<void> => {
    setIsSaving(true);

    try {
      const assignedManagerIds: string[] = assignedManagers.map(
        (strataManager: IStrataManager) => strataManager.userId
      );

      const payload: IUnitAssignmentUpdate[] = [
        {
          unitId: unit.unitId,
          assignedStrataManagers: assignedManagerIds,
        },
      ];

      const response: any = await putUnitStrataManagers(payload);

      if (response?.ok) {
        setIsDirty(false);
        toast.success(`${variables.strataManagers} updated for this common area.`);
      } else {
        toast.error(
          `Could not update ${variables.strataManagers}. Please try again.`
        );
      }
    } catch (error) {
      console.error('Failed to save strata manager assignments:', error);
      toast.error(
        `Could not update ${variables.strataManagers}. Please try again.`
      );
    } finally {
      setIsSaving(false);
    }
  };

  return {
    assignedManagers,
    isDirty,
    isSaving,
    addManagers,
    removeManager,
    save,
  };
};